import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { ChatAppResponse } from "@/api/models"

import { Eye, PanelRightClose, PanelRightOpen, Share2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { SidebarInset } from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import { useAITranslatorChat } from "@/features/ai-translator/hooks/use-ai-translator-chat"
import { useAITranslatorSettingsLocal } from "@/features/ai-translator/hooks/use-ai-translator-settings-local"
import { useTranslateSettingsPanel } from "@/features/ai-translator/hooks/use-translate-settings-panel"
import { Chat } from "@/features/chat/components/chat"
import { ChatShareDialog } from "@/features/chat/components/chat-share-dialog"
import { SharedHistoryMarkerLine } from "@/features/chat/components/shared-history-marker"
import { StartNewChat } from "@/features/chat/components/start-new-chat"
import { useChatHistory } from "@/features/chat/hooks/use-chat-history"
import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"
import { useSharedHistoryMarkers } from "@/features/chat/hooks/use-shared-history-markers"
import { getSessionErrorCode } from "@/features/chat/utils/session-error-code"
import { useContextMenuActions } from "@/hooks/use-context-menu-actions"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useMarkSessionNotificationViewed } from "@/hooks/use-session-sharing"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useAITranslatorStore } from "@/stores/ai-translator-store"
import { usePendingMessageStore } from "@/stores/pending-message-store"
import { getResponseContext } from "@/utils/response-format-utils"
import { formatTime } from "@/utils/time"

export function AITranslatorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getRoutePath } = useRoutes()
  const openExplorer = () => {
    void navigate(getRoutePath("open"))
  }

  // Determine session type from current route
  const sessionType = useCurrentSessionType()

  const translatorStore = useAITranslatorStore(sessionType)

  // Sync URL sessionId → ChatService at page level. Below the 1024px mobile
  // breakpoint the sidebar (and its ChatHistory) is an unmounted Sheet (#3515).
  useChatHistory({ sessionType, useChatStore: () => translatorStore })

  const { settings: translatorSettings } = useAITranslatorSettingsLocal()

  const {
    chatMessageStreamEnd,
    aborted,
    chatState,
    moduleName,
    handleSharedLeaveRedirect,
    estimatedTimeLeft,
    elapsedTime,
    currentSessionId,
  } = useAITranslatorChat()

  const { isSettingsPanelOpen, settingsSidebarTriggerLabel, toggleSettingsPanel } =
    useTranslateSettingsPanel()

  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareMessageId, setShareMessageId] = useState<string | undefined>()
  const handleManageSharing = useCallback((messageId?: string) => {
    setShareMessageId(messageId)
    setShowShareDialog(true)
  }, [])

  // Fetch sessions to check if current user is a reader
  const { data: sessions = [] } = useSessionsQuery(sessionType)

  // Check if current user is a reader (cannot send messages)
  const currentSession = useMemo(
    () => sessions.find((s) => s?.id === currentSessionId),
    [sessions, currentSessionId]
  )
  const isReader = currentSession?.currentUserRole === "reader"

  // Get current user's access mode from session participants
  const currentUserOid = SettingsService.getInstance().claims.oid
  const currentUserParticipant = useMemo(() => {
    if (!currentSession?.participants || !currentUserOid) return undefined

    // Get user's groups from settings service
    const userGroups = SettingsService.getInstance().claims.groups || []
    const userGroupsSet = new Set(userGroups)

    // Find participant: check direct user OR group membership
    return currentSession.participants.find(
      (p) =>
        (p.type === "user" && p.oid === currentUserOid) ||
        (p.type === "group" && userGroupsSet.has(p.oid))
    )
  }, [currentSession?.participants, currentUserOid])
  const currentUserAccessMode = currentUserParticipant?.messageAccessMode

  // Get current message ID (from latest message in chat)
  const answersToDisplay = useMemo(
    () =>
      chatState.streamingAnswer
        ? [...chatState.messages, chatState.streamingAnswer]
        : chatState.messages,
    [chatState.messages, chatState.streamingAnswer]
  )

  const { sharedHistoryMarkers } = useSharedHistoryMarkers(
    answersToDisplay,
    currentSession?.participants,
    currentUserParticipant
  )
  const currentMessageId = useMemo(() => {
    if (answersToDisplay.length === 0) return undefined
    const latestMessage = answersToDisplay[answersToDisplay.length - 1]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!latestMessage || !Array.isArray(latestMessage) || latestMessage.length < 2) {
      return undefined
    }
    const response: ChatAppResponse = latestMessage[1]
    return response.messageId ?? response.context.message_id ?? undefined
  }, [answersToDisplay])

  // Mark notification as viewed when user opens a shared session
  const markNotificationViewed = useMarkSessionNotificationViewed()
  const markedSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      currentSessionId &&
      currentSession?.hasUnviewedNotification &&
      markedSessionRef.current !== currentSessionId
    ) {
      markedSessionRef.current = currentSessionId
      markNotificationViewed.mutate(currentSessionId)
    }
    // Reset ref when session changes (so we can mark new session if needed)
    if (
      currentSessionId &&
      markedSessionRef.current !== currentSessionId &&
      !currentSession?.hasUnviewedNotification
    ) {
      markedSessionRef.current = currentSessionId
    }
  }, [currentSessionId, currentSession?.hasUnviewedNotification])

  // Handle pending message from "break out to new chat"
  const pendingMessage = usePendingMessageStore((state) => state.pendingMessage)
  const clearPendingMessage = usePendingMessageStore((state) => state.clearPendingMessage)

  // Context menu actions for break out functionality
  const { handleBreakOutChat: contextMenuBreakOut } = useContextMenuActions({
    currentSessionId,
    sessionType,
  })

  // Handler for breaking out from AI response button
  const handleBreakOutFromAIResponse = useCallback(
    async (content: string) => {
      try {
        logger.info("Break out button clicked", { contentLength: content.length })
        await contextMenuBreakOut(content, "user")
      } catch (error) {
        logger.error("Break out button failed", error as Error)
        toast.error("Failed to create new chat")
      }
    },
    [contextMenuBreakOut]
  )

  // Callback to handle sharing a specific message
  const handleShareMessage = useCallback((messageId?: string) => {
    setShareMessageId(messageId)
    setShowShareDialog(true)
  }, [])

  useEffect(() => {
    // Check if we have a pending message AND we're in a different session
    if (
      pendingMessage &&
      (currentSessionId !== pendingMessage.sourceSessionId || currentSessionId === null)
    ) {
      // Small delay to ensure the chat is fully rendered
      const timer = setTimeout(() => {
        logger.info("Processing pending assistant message", {
          textLength: pendingMessage.text.length,
          sourceSessionId: pendingMessage.sourceSessionId,
        })
        const statusLine = pendingMessage.sourceSessionId
          ? `🔗 Broken out from [${pendingMessage.sourceSessionTitle || "previous chat"}](/translate/chat/${pendingMessage.sourceSessionId})\n\n`
          : `🔗 Broken out from previous chat\n\n`
        const displayContent = statusLine + pendingMessage.text

        // Inject message into chat state
        const updateChatState = translatorStore.getState().changeChatState
        // Read chatState inside changeChatState to avoid overwriting concurrent updates
        const currentSessId = translatorStore.getState().currentSessionId
        const state = translatorStore.getState()
        const currentMessages =
          currentSessId && currentSessId in state.sessionStates
            ? state.sessionStates[currentSessId].messages
            : []
        updateChatState({
          messages: [
            ...currentMessages,
            [
              "", // Empty user message
              {
                content: displayContent,
                context: {
                  data_points: [],
                  followup_questions: null,
                  thoughts: [],
                },
                session_state: null,
                completed: true,
              },
            ],
          ],
        })

        toast.info("Chat created", {
          description: `Broken out from "${pendingMessage.sourceSessionTitle || "previous chat"}"`,
        })

        clearPendingMessage()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [pendingMessage, currentSessionId, clearPendingMessage])

  return (
    <Chat>
      <SidebarInset className="module-enter relative h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] overflow-hidden md:peer-data-[variant=inset]:mr-0">
        <Chat.Header className="absolute top-0 right-0 left-0 z-50 justify-between lg:justify-end">
          <div className="mr-auto flex items-center gap-2">
            <div className="lg:hidden">
              <StartNewChat onStartNewChatClick={openExplorer} />
            </div>
            {isReader && (
              <Badge
                className="border-foreground bg-accent text-accent-foreground h-7 border px-3"
                variant="outline"
              >
                <Eye className="mr-1.5 size-3" />
                {t("chatsession.reader_badge", { defaultValue: "READER" })}
              </Badge>
            )}
            {currentSessionId && (
              <Button
                className="h-7 gap-1.5 border-none px-3 shadow-none"
                size="sm"
                variant="ghost"
                onClick={() => setShowShareDialog(true)}
              >
                <Share2 className="size-3" />
                {t("sharing.share", { defaultValue: "Share" })}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={settingsSidebarTriggerLabel}
                  aria-pressed={isSettingsPanelOpen}
                  data-testid="translate-settings-sidebar-trigger"
                  size="icon"
                  type="button"
                  variant="ghost"
                  className={cn(
                    buttonVariants({
                      size: "icon",
                      variant: "icon",
                      className: "bg-background",
                    }),
                    "relative size-9 rounded-full",
                    isSettingsPanelOpen &&
                      "border-foreground bg-accent text-accent-foreground border"
                  )}
                  onClick={toggleSettingsPanel}
                >
                  {isSettingsPanelOpen ? (
                    <PanelRightClose className="size-5" />
                  ) : (
                    <PanelRightOpen className="size-5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{settingsSidebarTriggerLabel}</TooltipContent>
            </Tooltip>
          </div>
        </Chat.Header>
        <Chat.Content>
          {(() => {
            return (
              <>
                {answersToDisplay.length === 0 &&
                  !chatState.lastQuestion[0] &&
                  !chatState.isLoading &&
                  !chatState.isHydrating &&
                  !chatState.streamingAnswer && (
                    <div className="flex flex-col items-center gap-4 py-12">
                      <p>{t("translatorTool.historyHint")}</p>
                      <Button onClick={openExplorer}>{t("translatorTool.openExplorer")}</Button>
                    </div>
                  )}
                {sharedHistoryMarkers.top.length > 0 && (
                  <SharedHistoryMarkerLine
                    markers={sharedHistoryMarkers.top}
                    onManageSharing={handleManageSharing}
                  />
                )}
                {answersToDisplay.map((answer: [string, ChatAppResponse], index: number) => {
                  const shouldShowUserMessage =
                    currentUserAccessMode !== "specific_message" && answer[0] && answer[0].trim()

                  const answerKey =
                    getResponseContext(answer[1]).message_id || answer[1].messageId || index
                  const messageId = answer[1].messageId || getResponseContext(answer[1]).message_id
                  const beforeMarkers = messageId
                    ? sharedHistoryMarkers.before[messageId]
                    : undefined
                  const afterMarkers = messageId ? sharedHistoryMarkers.after[messageId] : undefined
                  return (
                    <section key={answerKey} className="flex flex-col gap-6">
                      {beforeMarkers?.length ? (
                        <SharedHistoryMarkerLine
                          markers={beforeMarkers}
                          onManageSharing={handleManageSharing}
                        />
                      ) : null}
                      {shouldShowUserMessage && <Chat.UserMessage message={answer[0]} />}
                      <Chat.SystemMessage
                        answer={answer[1]}
                        index={index}
                        isLastMessage={index === answersToDisplay.length - 1}
                        isReader={isReader}
                        messageId={answer[1].messageId}
                        moduleName={moduleName}
                        noCitation={true}
                        sessionId={currentSessionId ?? undefined}
                        sessionType={sessionType}
                        showAnalysisPanelIcon={true}
                        showCopyRaw={true}
                        showDownload={true}
                        showFactCheck={false}
                        showFollowupQuestions={false}
                        targetLanguage={translatorSettings.selectedLanguage}
                        isStreaming={
                          Boolean(chatState.streamingAnswer) &&
                          index === answersToDisplay.length - 1
                        }
                        onBreakOutChat={handleBreakOutFromAIResponse}
                        onShareMessage={handleShareMessage}
                      />
                      {afterMarkers?.length ? (
                        <SharedHistoryMarkerLine
                          markers={afterMarkers}
                          onManageSharing={handleManageSharing}
                        />
                      ) : null}
                    </section>
                  )
                })}
                {sharedHistoryMarkers.bottom.length > 0 && (
                  <SharedHistoryMarkerLine
                    markers={sharedHistoryMarkers.bottom}
                    onManageSharing={handleManageSharing}
                  />
                )}
              </>
            )
          })()}
          {chatState.isLoading && !chatState.streamingAnswer && (
            <>
              {currentUserAccessMode !== "specific_message" && chatState.lastQuestion[0] && (
                <Chat.UserMessage message={chatState.lastQuestion[0]} />
              )}
              <Chat.Loading
                citationSection={currentSessionId ?? undefined}
                message={
                  estimatedTimeLeft !== null
                    ? formatTime(estimatedTimeLeft, elapsedTime)
                    : t("pages.translate.answer_loading")
                }
              />
            </>
          )}
          {chatState.error ? (
            <>
              {currentUserAccessMode !== "specific_message" && chatState.lastQuestion[0] && (
                <Chat.UserMessage message={chatState.lastQuestion[0]} />
              )}
              <Chat.Error
                error={(chatState.error as Error).toString()}
                errorCode={getSessionErrorCode(chatState)}
                onRetry={openExplorer}
              />
            </>
          ) : null}
          {aborted ? (
            <>
              {currentUserAccessMode !== "specific_message" && aborted[0] && (
                <Chat.UserMessage message={aborted[0]} />
              )}
              <Chat.Error
                error={t("pages.common.request_aborted") + "\n\n" + (aborted[1] ? aborted[1] : "")}
                onRetry={openExplorer}
              />
            </>
          ) : null}
          <div ref={chatMessageStreamEnd} />
        </Chat.Content>
        <div className="flex justify-center px-4 py-4">
          <Button variant="outline" onClick={openExplorer}>
            {t("translatorTool.openExplorer")}
          </Button>
        </div>
      </SidebarInset>

      {currentSessionId && (
        <ChatShareDialog
          chatStore={translatorStore}
          currentMessageId={shareMessageId ?? currentMessageId}
          open={showShareDialog}
          session={currentSession}
          sessionId={currentSessionId}
          onLeave={handleSharedLeaveRedirect}
          onOpenChange={(open) => {
            setShowShareDialog(open)
            // Reset shareMessageId when dialog closes
            if (!open) {
              setShareMessageId(undefined)
            }
          }}
        />
      )}
    </Chat>
  )
}
